import { useEffect, useRef, useState } from "react";
import { Bell } from "lucide-react";
import api from "@/lib/api";
import { useMessaging } from "@/context/MessagingContext";

function timeAgo(iso) {
  if (!iso) return "";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "now";
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
}

/**
 * Bell icon + unread badge + dropdown of recent notifications.
 * `kinds` filters which conversation kinds appear (e.g. admin's patient/doctor bells).
 * Clicking a notification (or "Open inbox") calls onOpen(conversationId|null).
 */
export default function NotificationsBell({ count = 0, kinds = null, color = "#042C53", onOpen, testid = "notif-bell", label = "Notifications" }) {
  const { tick } = useMessaging();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const ref = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/conversations");
      let list = data.conversations || [];
      if (kinds) list = list.filter((c) => kinds.includes(c.kind));
      setItems(list);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  useEffect(() => { if (open) load(); }, [open, tick]); // eslint-disable-line react-hooks/exhaustive-deps

  const pick = (cid) => { setOpen(false); onOpen && onOpen(cid); };

  return (
    <div className="nb" ref={ref}>
      <button
        className="nb-btn"
        onClick={() => setOpen((v) => !v)}
        aria-label={label}
        title={label}
        data-testid={testid}
      >
        <Bell size={20} color={color} />
        {count > 0 && <span className="nb-badge" data-testid={`${testid}-badge`}>{count > 99 ? "99+" : count}</span>}
      </button>

      {open && (
        <div className="nb-menu" data-testid={`${testid}-menu`} style={{ "--nb-accent": color }}>
          <div className="nb-head"><span>{label}</span></div>
          <div className="nb-list">
            {items.length === 0 && <p className="nb-empty">No messages yet.</p>}
            {items.map((c) => (
              <button
                key={c.id}
                className={`nb-item ${c.unread > 0 ? "unread" : ""}`}
                onClick={() => pick(c.id)}
                data-testid={`notif-item-${c.id}`}
              >
                <span className="nb-dot" style={{ background: color, opacity: c.unread > 0 ? 1 : 0 }} />
                <span className="nb-item-main">
                  <span className="nb-item-top">
                    <b>{c.counterpart_name}</b>
                    <time>{timeAgo(c.last_at)}</time>
                  </span>
                  <span className="nb-item-prev" data-testid={`notif-text-${c.id}`}>
                    {c.unread > 0
                      ? `${c.unread === 1 ? "One" : c.unread} message${c.unread === 1 ? "" : "s"} from ${c.counterpart_name}`
                      : (c.last_text || "No messages yet")}
                  </span>
                </span>
                {c.unread > 0 && <span className="nb-cnt" style={{ background: color }}>{c.unread}</span>}
              </button>
            ))}
          </div>
          <button className="nb-foot" onClick={() => pick(null)} data-testid={`${testid}-open-inbox`}>Open inbox →</button>
        </div>
      )}
    </div>
  );
}
